import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Check, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { z } from "zod";

// Email validation schema with proper constraints
const emailSchema = z.string()
  .min(1, "Email is required")
  .email("Please enter a valid email address")
  .max(254, "Email address is too long")
  .transform(val => val.toLowerCase().trim());

interface WaitlistDialogProps {
  children: React.ReactNode;
}

export function WaitlistDialog({ children }: WaitlistDialogProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email with Zod schema
    const validationResult = emailSchema.safeParse(email);
    if (!validationResult.success) {
      toast.error(validationResult.error.errors[0].message);
      return;
    }
    
    const validEmail = validationResult.data;
    setStatus("loading");
    
    const { error } = await supabase
      .from('waitlist')
      .insert({ email: validEmail });

    if (error) {
      if (error.code === '23505') {
        toast.info("You're already on the waitlist!");
        setStatus("success");
      } else {
        toast.error("Something went wrong. Please try again.");
        setStatus("idle");
        return;
      }
    } else {
      setStatus("success");
    }
    
    setTimeout(() => {
      setOpen(false);
      setTimeout(() => {
        setStatus("idle");
        setEmail("");
      }, 300);
    }, 2000);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md bg-card/95 backdrop-blur-2xl border-white/10 p-0 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="p-8"
        >
          <button
            onClick={() => setOpen(false)}
            className="absolute right-4 top-4 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          <AnimatePresence mode="wait">
            {status === "success" ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="text-center py-8"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                  className="w-16 h-16 bg-lime/20 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <Check className="w-8 h-8 text-lime" />
                </motion.div>
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  {t('waitlist.success.title')}
                </h3>
                <p className="text-muted-foreground">
                  {t('waitlist.success.message')}
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-lime/20 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-lime" />
                  </div>
                  <span className="text-sm font-medium text-lime">
                    {t('hero.badge')}
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-foreground mb-2">
                  {t('waitlist.title')}
                </h3>
                <p className="text-muted-foreground mb-8">
                  {t('waitlist.subtitle')}
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('waitlist.email')}
                      required
                      className="w-full px-4 py-4 bg-secondary/50 border border-white/10 rounded-xl 
                               text-foreground placeholder:text-muted-foreground
                               focus:outline-none focus:border-lime/50 focus:ring-2 focus:ring-lime/20
                               transition-all duration-300"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>{t('waitlist.submitting')}</span>
                      </>
                    ) : (
                      <span>{t('waitlist.submit')}</span>
                    )}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
